import { useFormik } from "formik";
import Select from "react-select";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { retrieveCSRFToken } from "../../utils/ajax";
import { helpText } from "../form-fields/common";
import { Modal } from "../Modal";

const formSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(255, "Too Long!")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
  seeking: Yup.array().min(1, "Pick at least one").ensure(),
  message: Yup.string().min(10, "Too Short!").required("Required"),
});

const GetInvolvedModal = ({
  showModal,
  setShowModal,
  user,
  project,
  validSeekingOptions,
  validSeekingOptionMap,
}) => {
  const sendGetInvolvedMessage = async (
    params,
    { setSubmitting, setErrors }
  ) => {
    setSubmitting(true);

    try {
      const message = `
${params.name} is interested in helping with the following categories:
${params.seeking.map((v) => " * " + (validSeekingOptionMap[v] || v)).join("\n")}

${params.message}

${params.name},
${params.email}
      `.trim();
      const result = await fetch(
        `/api/message/thread/${project.created_by_user.id}/send/`,
        {
          method: "POST",
          headers: {
            "X-CSRFToken": retrieveCSRFToken(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subject: `${params.name} wants to get involved with ${project.name}`,
            message,
          }),
        }
      );

      const body = await result.json();

      if (result.status === 400) {
        const errors = Object.keys(body).reduce((acc, key) => {
          return { ...acc, [key]: body[key].join(" ") };
        }, {});

        setErrors(errors);
      } else if (result.status === 200) {
        console.log("send message!", result);
        setShowModal(false);
        toast.success("Message sent!");
      } else {
        console.error("unhandled case...");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validSeekingFormOptions = [
    ...validSeekingOptions,
    { value: "other", label: "Other" },
  ];

  const formik = useFormik({
    initialValues: {
      name: user.username,
      email: user.email,
      seeking: [],
      message: "",
    },
    onSubmit: sendGetInvolvedMessage,
    validationSchema: formSchema,
  });

  return (
    <Modal showModal={showModal} closeModal={() => setShowModal(false)}>
      <div className="_container">
        <h1 className="title">Get Involved</h1>

        <form onSubmit={formik.handleSubmit}>
          <div className="columns">
            <div className="column">
              <div className="field">
                <label className="label" htmlFor="name">
                  Full Name
                </label>
                <div className="control">
                  <input
                    className="input"
                    id="name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                  />
                </div>
                {helpText(formik, "name")}
              </div>
            </div>

            <div className="column">
              <div className="field">
                <label className="label" htmlFor="email">
                  Email
                </label>
                <div className="control">
                  <input
                    className="input"
                    id="email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                  />
                </div>
                {helpText(formik, "email")}
              </div>
            </div>
          </div>

          <div className="field">
            <label className="label" htmlFor="seeking">
              Area of interest:
            </label>
            <Select
              isClearable={true}
              isMulti={true}
              id="seeking"
              value={validSeekingFormOptions.filter((t) =>
                formik.values.seeking.includes(t.value)
              )}
              onChange={(e) =>
                formik.setFieldValue(
                  "seeking",
                  e.map((s) => s.value)
                )
              }
              onBlur={formik.handleBlur}
              clearValue={() => []}
              options={validSeekingFormOptions}
              /* Below is necessary because the bottom of the options list gets clipped by the modal */
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              menuPortalTarget={document.body}
            />
            {helpText(formik, "seeking")}
          </div>

          <div className="field">
            <label className="label" htmlFor="message">
              Your Message:
            </label>
            <div className="control">
              <textarea
                className="textarea"
                placeholder="Type your message here..."
                id="message"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.message}
              />
            </div>
            {helpText(formik, "message")}
          </div>

          <div className="buttons">
            <button
              type="submit"
              className="button is-success"
              disabled={!formik.dirty || formik.isSubmitting || !formik.isValid}
            >
              Send
            </button>
            <button
              type="button"
              className="button"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default GetInvolvedModal;
